/* eslint-disable @reatom/unit-naming-rule */
import { atom, onConnect, reaction } from '@reatom/framework'

import {
  reatomTradesTable as reatomTradesTable_,
  setMakerFilterEvent,
} from '@root/features/trade/tables/trades-table/atoms'
import {
  browserHistory,
  routerLocationAtom,
} from '@root/shared/lib/react-router'
import { reatomFilterByWalletEvent } from '@root/widgets/trade/trade-chart/state/reatom-trade-chart-maker-filter-event'

import { nextMakerSearch } from '../model/maker-search'

import type { PairModel } from '@root/entities/pair'

/**
 * Synced with maker filter in trades table. Also red for initial filter value
 */
export const tradeMakerFilterAtom = atom<string | undefined>(
  undefined,
  'tradeMakerFilterAtom',
)

export const reatomTradesTable = ({
  pair,
}: {
  pair: PairModel
}): TradesTableModel => {
  const model = reatomTradesTable_({
    pair,
    pairAddress: pair.address,
  })

  const syncMakerFilterReaction = reaction((ctx) => {
    const maker = ctx.spy(model.makerFilter)
    tradeMakerFilterAtom(ctx, maker)

    if (maker) {
      reatomFilterByWalletEvent(ctx, {
        walletAddress: maker,
        pairAddress: pair.address,
        network: pair.network,
      })
    } else {
      reatomFilterByWalletEvent(ctx, undefined)
    }

    // Only when the query string really differs. `replace` always builds a
    // new Location object and `routerLocationAtom` compares by reference, so
    // an unconditional call notified every subscriber — including the router
    // sync, which is the second `change-network` and a second pass over the
    // pair atoms. On a clean open `maker` is undefined and nothing changes.
    const location = ctx.get(routerLocationAtom)
    const search = nextMakerSearch(location.search, maker)
    if (search === undefined) {
      return
    }

    browserHistory.replace({ ...location, search })
  })

  onConnect(model, (ctx) => {
    model.setMakerFilter(ctx, ctx.get(tradeMakerFilterAtom))
    const unsubEvent = setMakerFilterEvent.onCall((ctx, { maker }) => {
      model.setMakerFilter(ctx, maker)
    })

    const sub = syncMakerFilterReaction(ctx)

    return () => {
      tradeMakerFilterAtom(ctx, undefined)
      unsubEvent()
      sub.unsubscribe()
    }
  })

  return model
}
export interface TradesTableModel
  extends ReturnType<typeof reatomTradesTable_> {}

export const syncQueryParamWithMakerReaction = reaction((ctx) => {
  const location = ctx.spy(routerLocationAtom)
  // TODO: Remove hardcoded paths
  if (location.pathname.includes('/trade/')) {
    const searchParams = new URLSearchParams(location.search)
    const maker = searchParams.get('maker')
    if (maker) {
      tradeMakerFilterAtom(ctx, maker)
    }
  }
}, 'syncQueryParamWithMakerReaction')
